import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inApp } from "@/lib/native";

// Chrome/Android hands us the install prompt to show when we like.
type BeforeInstallPromptEvent = Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: "accepted" | "dismissed" }> };

const DISMISSED = "install-dismissed";
const standalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios/i.test(navigator.userAgent);

/**
 * "Add cscoach to your home screen": Android gets the real install prompt,
 * iPhone gets the two taps Safari needs. Shown once per phone until dismissed,
 * and never inside the installed app.
 */
export function InstallPrompt() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [show, setShow] = useState(false);
  const [ios, setIOS] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = !!localStorage.getItem(DISMISSED);
    } catch { /* private window */ }
    if (dismissed || standalone() || inApp()) return; // already an app
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    if (isIOS()) {
      setIOS(true);
      // Not on the first visit: someone reading one match does not want a banner.
      const t = setTimeout(() => setShow(true), 20_000);
      return () => (clearTimeout(t), window.removeEventListener("beforeinstallprompt", onPrompt));
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(DISMISSED, String(Date.now()));
    } catch { /* fine */ }
  };
  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setShow(false);
    else dismiss();
  };

  if (!show) return null;
  return (
    <div className="fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-50 md:hidden animate-in slide-in-from-bottom-4">
      <div className="glass flex items-start gap-3 rounded-2xl border p-3.5 shadow-xl">
        <img src="/icons/icon-192.png" alt="" className="size-11 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-display font-semibold">cscoach как приложение</div>
          {ios ? (
            <p className="mt-0.5 text-muted-foreground">
              Нажмите <Share className="inline size-3.5 -translate-y-px" /> <b>Поделиться</b>, затем <b>На экран «Домой»</b>.
            </p>
          ) : (
            <p className="mt-0.5 text-muted-foreground">Иконка на экране, открывается без браузера, работает офлайн.</p>
          )}
          {!ios && (
            <Button size="sm" className="mt-2" onClick={install}><Download className="size-4" /> Установить</Button>
          )}
        </div>
        <button type="button" aria-label="Закрыть" onClick={dismiss} className="-m-1 rounded-md p-1 text-muted-foreground hover:text-foreground">
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
